import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import MediaCard from "../components/MediaCard";
import api, { resolveMediaUrl } from "../lib/api";
import { useZoom } from "../context/ZoomContext";

const TYPES = ["all", "image", "video", "pdf", "audio", "embed"];

export default function DossierDetail() {
  const { slug } = useParams();
  const [dossier, setDossier] = useState(null);
  const [items, setItems] = useState([]);
  const [years, setYears] = useState([]);
  const [tags, setTags] = useState([]);
  const [type, setType] = useState("all");
  const [year, setYear] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const { gridClass } = useZoom();

  useEffect(() => {
    api.get(`/dossiers/${slug}`).then((r) => setDossier(r.data)).catch(() => setDossier({ slug, title: slug, description: "" }));
    api.get("/years", { params: { dossier: slug } }).then((r) => setYears(r.data || []));
    api.get("/tags", { params: { dossier: slug } }).then((r) => setTags(r.data || []));
  }, [slug]);

  useEffect(() => {
    const params = { dossier: slug, limit: 500 };
    if (type !== "all") params.media_type = type;
    if (year) params.year = year;
    if (tag) params.tag = tag;
    if (q) params.q = q;
    api.get("/media", { params }).then((r) => setItems(r.data || []));
  }, [slug, type, year, tag, q]);

  const clear = () => { setType("all"); setYear(""); setTag(""); setQ(""); };

  return (
    <Layout>
      <section className="px-4 md:px-6 pt-8 pb-6">
        <Link to="/dossiers" data-testid="dossier-back" className="pill pill-sm">← All dossiers</Link>
        <div className="flex items-end justify-between flex-wrap gap-6 mt-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="badge" data-testid="dossier-slug-badge">/{slug}</span>
              <span className="badge">{String(dossier?.item_count || 0).padStart(3, "0")} items</span>
            </div>
            <h1 className="heavy text-5xl sm:text-6xl md:text-8xl leading-[0.9] break-words">{dossier?.title || slug}</h1>
            {dossier?.description && (
              <p className="mono text-[13px] text-ink-muted mt-5 max-w-[58ch]">{dossier.description}</p>
            )}
          </div>
          {dossier?.cover_image && (
            <div className="w-40 h-40 md:w-56 md:h-56 tile shrink-0">
              <img src={resolveMediaUrl(dossier.cover_image)} alt={dossier.title} className="tile-media" />
            </div>
          )}
        </div>
      </section>

      {/* Filter pills */}
      <div className="hairline-t hairline-b px-4 md:px-6 py-4 flex flex-wrap items-center gap-2" data-testid="dossier-filter-bar">
        <span className="label-overline mr-2">// Filter</span>
        {TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            data-testid={`filter-type-${t}`}
            className={`pill pill-sm ${type === t ? "pill-active" : ""}`}
          >{t}</button>
        ))}
        <span className="mx-1 text-ink-faint">·</span>
        <select data-testid="filter-year" value={year} onChange={(e) => setYear(e.target.value)} className="input-mono w-auto">
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select data-testid="filter-tag" value={tag} onChange={(e) => setTag(e.target.value)} className="input-mono w-auto">
          <option value="">All tags</option>
          {tags.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex-1 min-w-[200px]">
          <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search within…" className="input-mono" />
        </div>
        {(type !== "all" || year || tag || q) && (
          <button onClick={clear} className="pill pill-sm">Clear</button>
        )}
      </div>

      <section className="px-2 md:px-3 py-6">
        {items.length === 0 ? (
          <div className="hairline p-12 text-center mx-2">
            <div className="heavy text-3xl">No items in this dossier.</div>
          </div>
        ) : (
          <div data-testid="dossier-grid" className={`grid ${gridClass} gap-2 md:gap-3`}>
            {items.map((m) => <MediaCard key={m.id} media={m} />)}
          </div>
        )}
      </section>
    </Layout>
  );
}
